import { useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from 'utils/cn'

type SnackbarVariant = 'info' | 'success' | 'warning' | 'error'
type SnackbarPosition =
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'top-right'
  | 'top-left'
  | 'top-center'

type SnackbarProps = {
  open: boolean
  message: ReactNode
  variant?: SnackbarVariant
  action?: ReactNode
  onClose?: () => void
  autoHideDuration?: number
  position?: SnackbarPosition
  className?: string
}

const positionClassName: Record<SnackbarPosition, string> = {
  'bottom-right': 'inset-x-0 bottom-6 justify-end',
  'bottom-left': 'inset-x-0 bottom-6 justify-start',
  'bottom-center': 'inset-x-0 bottom-6 justify-center',
  'top-right': 'inset-x-0 top-6 justify-end',
  'top-left': 'inset-x-0 top-6 justify-start',
  'top-center': 'inset-x-0 top-6 justify-center',
}

const variantClassName: Record<SnackbarVariant, string> = {
  info: 'border-border/70 bg-surface text-ink',
  success: 'border-accent/20 bg-accent-soft text-ink',
  warning: 'border-amber-200 bg-amber-50 text-amber-800',
  error: 'border-danger-border bg-danger-soft text-danger',
}

const indicatorClassName: Record<SnackbarVariant, string> = {
  info: 'bg-accent',
  success: 'bg-accent',
  warning: 'bg-amber-500',
  error: 'bg-danger',
}

// スワイプで消すと判定する移動量(px)
const DISMISS_THRESHOLD = 40

export function Snackbar({
  open,
  message,
  variant = 'info',
  action,
  onClose,
  autoHideDuration,
  position = 'bottom-right',
  className,
}: SnackbarProps) {
  const isTop = position.startsWith('top')
  const canDismiss = Boolean(onClose)
  const [dragOffset, setDragOffset] = useState(0)
  const startYRef = useRef<number | null>(null)
  // 終了判定はクロージャ越しの state ではなく ref で現在値を読む
  const offsetRef = useRef(0)

  useEffect(() => {
    if (!open || !autoHideDuration) return
    const timer = setTimeout(() => {
      onClose?.()
    }, autoHideDuration)
    return () => clearTimeout(timer)
  }, [autoHideDuration, onClose, open])

  // 再表示のたびにドラッグ位置をリセット
  useEffect(() => {
    if (open) {
      offsetRef.current = 0
      setDragOffset(0)
    }
  }, [open])

  if (!open || typeof document === 'undefined') return null

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!canDismiss) return
    startYRef.current = event.clientY
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (startYRef.current === null) return
    const delta = event.clientY - startYRef.current
    // top配置は上方向(負)、bottom配置は下方向(正)へのみ追従させる
    const limited = isTop ? Math.min(0, delta) : Math.max(0, delta)
    offsetRef.current = limited
    setDragOffset(limited)
  }

  const handlePointerEnd = () => {
    if (startYRef.current === null) return
    startYRef.current = null
    if (Math.abs(offsetRef.current) > DISMISS_THRESHOLD) {
      onClose?.()
      return
    }
    offsetRef.current = 0
    setDragOffset(0)
  }

  const isDragging = startYRef.current !== null

  return createPortal(
    <div
      className={cn(
        'pointer-events-none fixed z-50 flex w-full px-4 sm:px-6',
        positionClassName[position],
      )}
      role="status"
      aria-live="polite"
    >
      <div
        data-testid="snackbar"
        className={cn(
          'pointer-events-auto inline-flex max-w-[420px] items-start gap-3 rounded-2xl border px-4 py-3 text-sm shadow-card motion-safe:animate-fade-up',
          canDismiss && 'cursor-grab touch-none select-none active:cursor-grabbing',
          variantClassName[variant],
          className,
        )}
        style={
          canDismiss
            ? {
                transform: dragOffset
                  ? `translateY(${dragOffset}px)`
                  : undefined,
                opacity: dragOffset
                  ? Math.max(0.25, 1 - Math.abs(dragOffset) / 140)
                  : undefined,
                transition: isDragging
                  ? undefined
                  : 'transform 150ms ease, opacity 150ms ease',
              }
            : undefined
        }
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      >
        <span
          className={cn(
            'mt-1 h-2 w-2 flex-shrink-0 rounded-full',
            indicatorClassName[variant],
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1 leading-relaxed">{message}</div>
        {action && <div className="ml-2 flex-shrink-0">{action}</div>}
      </div>
    </div>,
    document.body,
  )
}
